"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { categoryLabel } from "@/lib/analytics";
import { formatMoney } from "@/lib/format";
import { SelectedStartupTicker } from "@/components/dashboard/metrics-games/selected-startup-ticker";
import { TickerItem } from "@/components/landing/ticker-item";
import { useTenKGame } from "@/components/dashboard/metrics-games/tenk-game-context";
import type { StartupRecord } from "@/lib/types";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_LENGTH = 2;

type Slot = "search" | "selected" | "both";

type Props = {
  slot?: Slot;
};

export function TenKStartupSelector({ slot = "both" }: Props) {
  const ctx = useTenKGame();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<StartupRecord[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadLoading, setLoadLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchSearchResults = useCallback(
    async (query: string) => {
      if (query.length < SEARCH_MIN_LENGTH) {
        setSearchResults([]);
        return;
      }
      setSearchLoading(true);
      try {
        const params = new URLSearchParams({
          search: query,
          limit: "20",
          forGame: "game-6"
        });
        if (ctx?.categoryFilter) {
          params.set("category", ctx.categoryFilter);
        }
        const res = await fetch(`/api/startups?${params.toString()}`, {
          cache: "no-store"
        });
        if (!res.ok) {
          setSearchResults([]);
          return;
        }
        const json = await res.json();
        setSearchResults(json.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [ctx?.categoryFilter]
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchSearchResults(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchSearchResults]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSelectStartup(slug: string, record?: StartupRecord) {
    if (!ctx) return;
    setLoadLoading(true);
    setShowDropdown(false);
    setSearchQuery("");
    setSearchResults([]);
    try {
      const res = await fetch(`/api/startups/${encodeURIComponent(slug)}/tenk`, {
        cache: "no-store"
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.details ?? "Failed to load startup");
      }
      const json = await res.json();
      ctx.setTenKInputs(json.inputs);
      const startupRecord = json.startup as StartupRecord;
      ctx.setSelectedStartupForTenK(startupRecord);
    } catch (err) {
      console.error("Failed to load startup for $10k game:", err);
    } finally {
      setLoadLoading(false);
    }
  }

  if (!ctx) return null;

  const showSearch = slot === "search" || slot === "both";
  const showSelected = slot === "selected" || slot === "both";

  async function handleLoadRandom() {
    if (!ctx) return;
    setLoadLoading(true);
    setShowDropdown(false);
    setSearchQuery("");
    setSearchResults([]);
    try {
      let startups: StartupRecord[];
      if (ctx.categoryFilter) {
        const res = await fetch(
          `/api/startups?category=${encodeURIComponent(ctx.categoryFilter)}`,
          { cache: "no-store" }
        );
        if (!res.ok) throw new Error("Failed to load startups");
        const json = await res.json();
        startups = json.data ?? [];
      } else {
        const res = await fetch("/api/market-ticker", { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to load startups");
        const json = await res.json();
        startups = json.data ?? [];
      }
      if (startups.length === 0) throw new Error("No startups available");
      const random = startups[Math.floor(Math.random() * startups.length)];
      await handleSelectStartup(random.slug, random);
    } catch (err) {
      console.error("Failed to load random startup:", err);
    } finally {
      setLoadLoading(false);
    }
  }

  const searchBlock = showSearch && (
    <div className="tenk-selector tenk-selector--search" ref={dropdownRef}>
      <div className="tenk-selector-input-wrap">
        <input
          type="text"
          className="form-input tenk-selector-input"
          placeholder={
            ctx.categoryFilter
              ? "Search startups in this niche or load a random one…"
              : "Search your startup or load a random one…"
          }
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
        />
        {searchLoading && <span className="tenk-selector-spinner" aria-hidden />}
      </div>
      {showDropdown && searchResults.length > 0 && (
        <ul className="tenk-selector-dropdown" role="listbox">
          {searchResults.map((s) => (
            <li
              key={s.slug}
              role="option"
              className="tenk-selector-option"
              onClick={() => handleSelectStartup(s.slug, s)}
            >
              <div className="ticker-startup">
                {s.icon && (
                  <img src={s.icon} alt="" className="ticker-logo" width={24} height={24} />
                )}
                <span className="ticker-name">{s.name}</span>
              </div>
              <span className="ticker-badge">{categoryLabel(s.category)}</span>
              <span className="tenk-selector-option-mrr">{formatMoney(s.mrr)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="tenk-selector-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleLoadRandom}
          disabled={loadLoading}
        >
          Load random
        </button>
      </div>
      {loadLoading && <p className="tenk-selector-loading">Loading startup data…</p>}
    </div>
  );

  const selectedBlock = showSelected && ctx.selectedStartupForTenK && (
    <div className="tenk-selector tenk-selector--centered">
      <SelectedStartupTicker
        slug={ctx.selectedStartupForTenK.slug}
        initialData={ctx.selectedStartupForTenK}
        onClear={() => ctx.setSelectedStartupForTenK(null)}
      />
    </div>
  );

  if (slot === "search") return searchBlock;
  if (slot === "selected") return selectedBlock;

  return (
    <div className="tenk-selector tenk-selector--centered" ref={dropdownRef}>
      <div className="tenk-selector-row">
        <div className="tenk-selector-input-wrap">
          <input
            type="text"
            className="form-input tenk-selector-input"
            placeholder={
              ctx.categoryFilter
                ? "Search startups in this niche or load a random one…"
                : "Search your startup or load a random one…"
            }
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          />
          {searchLoading && <span className="tenk-selector-spinner" aria-hidden />}
        </div>
        {ctx.selectedStartupForTenK && (
          <SelectedStartupTicker
            slug={ctx.selectedStartupForTenK.slug}
            initialData={ctx.selectedStartupForTenK}
            onClear={() => ctx.setSelectedStartupForTenK(null)}
          />
        )}
      </div>
      {showDropdown && searchResults.length > 0 && (
        <ul className="tenk-selector-dropdown" role="listbox">
          {searchResults.map((s) => (
            <li
              key={s.slug}
              role="option"
              className="tenk-selector-option"
              onClick={() => handleSelectStartup(s.slug, s)}
            >
              <div className="ticker-startup">
                {s.icon && (
                  <img src={s.icon} alt="" className="ticker-logo" width={24} height={24} />
                )}
                <span className="ticker-name">{s.name}</span>
              </div>
              <span className="ticker-badge">{categoryLabel(s.category)}</span>
              <span className="tenk-selector-option-mrr">{formatMoney(s.mrr)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="tenk-selector-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleLoadRandom}
          disabled={loadLoading}
        >
          Load random
        </button>
      </div>
      {loadLoading && <p className="tenk-selector-loading">Loading startup data…</p>}
    </div>
  );
}
